import { Controller } from "@hotwired/stimulus"

/**
 * Writer Document Context Controller
 *
 * Keeps the Assistant anchored to the document currently open in the Writer.
 *
 * The assistant injects `<documento_actual>` into every message from the
 * conversation's stored context, which is refreshed server-side from the
 * `prompt_params[writer_document_id]` of each POST. The server-rendered hidden
 * input for that param is frozen at frame-render time, so it goes stale when
 * the user switches documents or reloads/resumes a conversation.
 *
 * This controller sets the param live at submit time from the single source of
 * truth (the editor URL, `/redactor/:id/edit`), so every message carries the
 * document that is actually open. Off the Writer it does nothing.
 *
 * It hooks `turbo:submit-start` (the reliable hook for this Turbo form, where
 * the request body is already a FormData) and also `formdata` (for any native
 * submission). Both mutate the outgoing payload directly, so they win over the
 * stale hidden input regardless of which path the browser takes.
 */
export default class extends Controller {
  static EDIT_PATH = /^\/redactor\/([^/]+)\/edit\b/
  static targets = ["selection", "selectionText", "input"]
  static values = { selectionPlaceholder: String }

  connect() {
    this.boundFormData = (event) => this.applyTo(event.formData)
    this.boundSubmitStart = (event) => this.applyTo(event.detail?.formSubmission?.body)
    this.boundSelectionChange = (event) => this.showSelection(event.detail?.text)
    this.element.addEventListener("formdata", this.boundFormData)
    this.element.addEventListener("turbo:submit-start", this.boundSubmitStart)
    window.addEventListener("editor:selection-change", this.boundSelectionChange)
    window.dispatchEvent(new CustomEvent("editor:selection-query"))
  }

  disconnect() {
    this.element.removeEventListener("formdata", this.boundFormData)
    this.element.removeEventListener("turbo:submit-start", this.boundSubmitStart)
    window.removeEventListener("editor:selection-change", this.boundSelectionChange)
  }

  showSelection(text) {
    if (!this.hasSelectionTarget) return

    const shown = Boolean(text) && Boolean(this.currentWriterDocumentId())
    this.selectionTarget.hidden = !shown
    this.selectionTextTarget.textContent = shown ? `«${text}»` : ""
    if (!this.hasInputTarget || !this.selectionPlaceholderValue) return

    this.defaultPlaceholder ??= this.inputTarget.placeholder
    this.inputTarget.placeholder = shown ? this.selectionPlaceholderValue : this.defaultPlaceholder
  }

  dropSelection() {
    window.dispatchEvent(new CustomEvent("editor:drop-selection"))
    if (this.hasInputTarget) this.inputTarget.focus()
  }

  applyTo(formData) {
    if (!formData || typeof formData.set !== "function") return

    const documentId = this.currentWriterDocumentId()
    if (!documentId) return

    formData.set("prompt_params[writer_document_id]", documentId)

    const selection = this.element.dataset.writerSelection
    if (selection) formData.set("prompt_params[writer_selection]", selection)
    else formData.delete("prompt_params[writer_selection]")
  }

  // Single source of truth: the editor URL. Fall back to the document layout's
  // data attribute (rendered from writer_document.id) if the path doesn't match.
  currentWriterDocumentId() {
    const match = window.location.pathname.match(this.constructor.EDIT_PATH)
    if (match) return decodeURIComponent(match[1])

    return document.getElementById("document-layout")
      ?.dataset.writerSidebarTabsWriterDocumentIdValue || null
  }
}
